/**
 * WinningStrategySection — landing-page module for "Claire's Winning Strategy".
 * Talks to the same audience as B2CRadarSection but presents the AI competitive
 * playbook (senior consultant analysis + copy-ready outreach) using Claude 4.5.
 */
import { Brain, Trophy, Mail, Target, ShieldCheck, Zap, Sparkles } from "lucide-react";

const COPY = {
  pt: {
    pill: "Novo · Claire's Winning Strategy",
    title: "Uma consultora sênior de vendas dentro de cada oportunidade B2B & Gov",
    sub: "A Claire lê o edital, cruza com o histórico do seu funil e devolve um plano de ataque acionável — junto com o e-mail pronto pra você mandar hoje.",
    benefits: [
      { icon: Brain, t: "Análise executiva",
        d: "Resumo do porquê essa licitação/lead vale seu tempo, com contexto do órgão comprador ou do decisor B2B." },
      { icon: Target, t: "Ponto-chave estratégico",
        d: "O ângulo que a maioria dos concorrentes ignora — a diferença entre ganhar e virar mais um envelope descartado." },
      { icon: Trophy, t: "Tática de vendas",
        d: "Passos concretos: quem contatar primeiro, qual credencial destacar, qual objeção antecipar." },
      { icon: Mail, t: "E-mail pronto pra enviar",
        d: "Assunto + corpo em português profissional, personalizado, sem soar template. Só copiar e enviar." },
      { icon: ShieldCheck, t: "Persistente e auditável",
        d: "A estratégia fica salva no card — sua equipe volta a ver o plano, o histórico e quando foi gerado." },
      { icon: Zap, t: "10 créditos por análise",
        d: "Sem mensalidade extra. Cada Winning Strategy custa só 10 créditos — o mesmo da economia interna do AgenteLead Pro." },
    ],
    howTitle: "Como a Claire monta a estratégia",
    steps: [
      "Você abre um lead (B2B) ou uma licitação (Gov) no CRM.",
      "Clica em \"Gerar Estratégia de Vitória\" — 10 créditos.",
      "Claire cruza título, agência, NAICS, valor, prazo e Victory Index.",
      "Claude 4.5 devolve executive summary, key point, tática e e-mail.",
      "Você copia, envia e ganha 30-60 minutos por lead trabalhado.",
    ],
    cta1: "Testar Winning Strategy grátis",
    cta2: "Ver Kanban Gov",
  },
  en: {
    pill: "New · Claire's Winning Strategy",
    title: "A senior sales consultant sitting inside every B2B & Gov opportunity",
    sub: "Claire reads the RFP, crosses it with your pipeline history and returns an actionable game plan — plus a ready-to-send outreach email.",
    benefits: [
      { icon: Brain, t: "Executive analysis",
        d: "A summary of why this bid or lead deserves your time, with buyer/decider context." },
      { icon: Target, t: "Strategic key point",
        d: "The angle most competitors miss — the difference between winning and being just another envelope." },
      { icon: Trophy, t: "Sales tactic",
        d: "Concrete steps: who to contact first, which credential to highlight, which objection to preempt." },
      { icon: Mail, t: "Ready-to-send email",
        d: "Subject + body, personalized, professional — no template smell. Copy, send, done." },
      { icon: ShieldCheck, t: "Persistent and auditable",
        d: "The strategy stays on the card. Your team can revisit the plan, history and generation timestamp anytime." },
      { icon: Zap, t: "10 credits per analysis",
        d: "No extra subscription. Each Winning Strategy costs just 10 credits from your internal balance." },
    ],
    howTitle: "How Claire builds the plan",
    steps: [
      "Open a lead (B2B) or opportunity (Gov) inside the CRM.",
      "Click \"Generate Winning Strategy\" — 10 credits.",
      "Claire cross-references title, agency, NAICS, value, deadline and Victory Index.",
      "Claude 4.5 returns executive summary, key point, tactic and email.",
      "Copy, send and save 30-60 minutes per opportunity worked.",
    ],
    cta1: "Try Winning Strategy free",
    cta2: "See the Gov Kanban",
  },
  es: {
    pill: "Nuevo · Winning Strategy de Claire",
    title: "Una consultora sénior de ventas dentro de cada oportunidad B2B y Gov",
    sub: "Claire lee la licitación, la cruza con tu histórico de pipeline y devuelve un plan de ataque accionable — con el correo listo para enviar hoy.",
    benefits: [
      { icon: Brain, t: "Análisis ejecutivo",
        d: "Resumen del porqué esta licitación/lead merece tu tiempo, con contexto del comprador o decisor B2B." },
      { icon: Target, t: "Punto clave estratégico",
        d: "El ángulo que la mayoría de competidores ignora — la diferencia entre ganar y ser otro sobre descartado." },
      { icon: Trophy, t: "Táctica de ventas",
        d: "Pasos concretos: a quién contactar primero, qué credencial destacar, qué objeción anticipar." },
      { icon: Mail, t: "Correo listo para enviar",
        d: "Asunto + cuerpo en español profesional, personalizado, sin sonar a plantilla. Solo copiar y enviar." },
      { icon: ShieldCheck, t: "Persistente y auditable",
        d: "La estrategia queda guardada en la tarjeta — tu equipo revisa el plan, el historial y cuándo se generó." },
      { icon: Zap, t: "10 créditos por análisis",
        d: "Sin mensualidad extra. Cada Winning Strategy cuesta solo 10 créditos de tu balance interno." },
    ],
    howTitle: "Cómo Claire arma la estrategia",
    steps: [
      "Abres un lead (B2B) o licitación (Gov) en el CRM.",
      "Haces click en \"Generar Estrategia de Victoria\" — 10 créditos.",
      "Claire cruza título, agencia, NAICS, valor, plazo y Victory Index.",
      "Claude 4.5 devuelve executive summary, key point, táctica y correo.",
      "Copias, envías y ahorras 30-60 minutos por oportunidad trabajada.",
    ],
    cta1: "Probar Winning Strategy gratis",
    cta2: "Ver Kanban Gov",
  },
};

export default function WinningStrategySection({ lang = "pt" }) {
  const c = COPY[lang] || COPY.pt;
  return (
    <section id="winning-strategy" data-testid="landing-winning-strategy"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "radial-gradient(ellipse at bottom, #241548 0%, #160C2D 55%, #0A051F 100%)", color: "#F8FAFC" }}>
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 right-1/4 w-[600px] h-[600px] rounded-full opacity-25 blur-3xl"
             style={{ background: "radial-gradient(circle, #A855F7 0%, transparent 60%)" }} />
        <div className="absolute -bottom-40 left-1/4 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl"
             style={{ background: "radial-gradient(circle, #F59E0B 0%, transparent 60%)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-fuchsia-300 mb-4">
            <Sparkles className="w-3.5 h-3.5" /> {c.pill}
          </span>
          <h2 className="text-[34px] sm:text-[42px] lg:text-[52px] font-extrabold leading-[1.05] mb-4 text-white">
            {c.title}
          </h2>
          <p className="text-[16.5px] sm:text-[18px] text-slate-300 max-w-2xl mx-auto">
            {c.sub}
          </p>
        </div>

        {/* Sample card mock — visual anchor for what Claire delivers */}
        <div className="mb-14 max-w-3xl mx-auto rounded-3xl border border-fuchsia-400/25 bg-gradient-to-br from-fuchsia-500/10 via-white/5 to-transparent p-6 lg:p-8 backdrop-blur">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-fuchsia-500/20 flex items-center justify-center ring-1 ring-fuchsia-400/40">
              <Brain className="w-5 h-5 text-fuchsia-300" />
            </div>
            <div>
              <div className="text-[13px] font-bold text-white">Claire · Senior Sales Consultant</div>
              <div className="text-[11px] text-fuchsia-200/80">Powered by Claude 4.5 · custa 10 créditos</div>
            </div>
          </div>
          <div className="space-y-3 text-[13.5px] text-slate-200 leading-relaxed">
            <div>
              <div className="text-[10px] uppercase font-bold tracking-widest text-fuchsia-300 mb-1">Executive summary</div>
              <p>{lang === "pt"
                ? "Marine Corps Prepositioning Program (MCPP) realiza Industry Day antes de licitação futura. Prazo longo indica oportunidade para preparar credenciais logísticas navais."
                : lang === "es"
                ? "Marine Corps Prepositioning Program (MCPP) realiza Industry Day antes de licitación. Plazo largo permite preparar credenciales logísticas navales."
                : "Marine Corps Prepositioning Program (MCPP) hosts an Industry Day before the actual solicitation. The long lead time is an opportunity to shape naval-logistics credentials."}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-widest text-fuchsia-300 mb-1">Key point</div>
              <p>{lang === "pt"
                ? "Presença física no Industry Day é CRÍTICA — Navy usa esses eventos para pré-qualificar fornecedores sérios."
                : lang === "es"
                ? "La presencia física en el Industry Day es CRÍTICA — la Navy usa esos eventos para precalificar proveedores serios."
                : "In-person Industry Day attendance is CRITICAL — the Navy uses these events to pre-qualify serious vendors."}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-widest text-fuchsia-300 mb-1">Sales tactic</div>
              <p>{lang === "pt"
                ? "Demonstrar experiência em gestão de inventário militar marítimo, certificações DoD ativas e capacidade 24/7 em múltiplos teatros."
                : lang === "es"
                ? "Demostrar experiencia en gestión de inventario militar marítimo, certificaciones DoD activas y capacidad 24/7 en múltiples teatros."
                : "Show track record in maritime military inventory ops, active DoD clearances and 24/7 multi-theater capacity."}</p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {c.benefits.map((b, i) => (
            <div key={b.t}
              data-testid={`ws-benefit-${i}`}
              className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 hover:border-fuchsia-400/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-fuchsia-500/15 flex items-center justify-center mb-4 ring-1 ring-fuchsia-400/30">
                <b.icon className="w-5 h-5 text-fuchsia-300" />
              </div>
              <h3 className="font-bold text-[16px] mb-1.5 text-white">{b.t}</h3>
              <p className="text-[13.5px] text-slate-300 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-8 lg:p-12 backdrop-blur">
          <div className="flex items-center gap-3 mb-8">
            <Trophy className="w-6 h-6 text-fuchsia-300" />
            <h3 className="text-[24px] sm:text-[28px] font-extrabold text-white">{c.howTitle}</h3>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {c.steps.map((s, i) => (
              <li key={i} data-testid={`ws-step-${i}`}
                className="relative rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-[10.5px] font-mono text-fuchsia-300 uppercase tracking-widest font-bold mb-2">
                  {lang === "pt" ? "Passo" : lang === "es" ? "Paso" : "Step"} {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-[13px] text-slate-200 leading-relaxed">{s}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* CTAs */}
        <div className="text-center mt-10">
          <a href="/login" data-testid="ws-cta-1"
             className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-[15px] mr-3 mb-3"
             style={{ background: "linear-gradient(135deg,#A855F7 0%,#EC4899 100%)", color: "#FFFFFF" }}>
            {c.cta1}
          </a>
          <a href="/login?next=/gov-kanban" data-testid="ws-cta-2"
             className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-[15px] border border-white/20 text-white hover:bg-white/5">
            {c.cta2}
          </a>
        </div>
      </div>
    </section>
  );
}
